import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { BrandCategoryPanel } from "@/features/catalog/components/brand-category-panel";
import { BrandNearMatches } from "@/features/catalog/components/brand-near-matches";
import { MergeBrandDialog } from "@/features/catalog/components/merge-brand-dialog";
import { ParentBrandField } from "@/features/catalog/components/parent-brand-field";
import { useEditorExitGuard } from "@/features/catalog/lib/use-editor-exit-guard";
import {
	type AdminBrandDetail,
	type BrandInput,
	createAdminBrand,
	getAdminBrand,
	updateAdminBrand,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { hasPermission } from "@/lib/permissions";

/**
 * The brand editor: the eight original fields plus plan 006's brand master
 * (issue #298).
 *
 * WHY THE MASTER FIELDS SIT IN THEIR OWN SECTION. They are research output, not
 * settings. Plan 006 calls its own brand master "research output requiring
 * verification, not load-ready data", and `tierBasis` / `tierAsOfDate` exist so
 * a segment is attributable rather than an unsourced opinion. Mixed in with
 * name and website they would read as equally settled facts.
 */

const UNSET = "__unset__";

/** Every field this screen can edit, and nothing else on the brand row. */
interface BrandForm {
	name: string;
	website: string;
	contactEmail: string;
	contactPhone: string;
	segment: AdminBrandDetail["segment"];
	positioning: AdminBrandDetail["positioning"];
	tierBasis: AdminBrandDetail["tierBasis"];
	/**
	 * WHEN the segment judgement was made, as `YYYY-MM-DD`.
	 *
	 * A string rather than a Date because that is what the input yields and what
	 * the endpoint coerces; converting here would only give two representations
	 * of the same value a chance to disagree.
	 */
	tierAsOfDate: string;
	manufacturer: string;
	countryOrigin: string;
	isImported: boolean;
	/** Another brand row, or null for none. */
	parentBrandId: string | null;
	/** Free-text channels, one per line in the box and an array on the wire. */
	distribution: string;
	isActive: boolean;
}

/**
 * ONLY what the operator touched. A key that is absent was never edited.
 *
 * This is the whole design, and it is why there is no effect in this file. The
 * screen used to copy the fetched row into state and PATCH the copy back, which
 * meant the request body was "everything the form knows" and had to be narrowed
 * by hand to avoid sending fields nobody edited. That hand-maintained list is
 * what went wrong: `isActive` stayed on it and cost a permission nobody had
 * spent, and `description` was sent as null by a screen with no description box.
 *
 * Storing only edits makes the body a fact rather than a policy -- it IS the
 * draft. A field with no control cannot appear in it, and a field the operator
 * did not touch cannot either, so neither defect has anywhere to live. Adding a
 * control later needs no second edit to keep a list in sync.
 *
 * `key in draft` is the test, not a truthiness or `??` check: `null` is a
 * legitimate edited value here -- it is how "Not set" clears a research field --
 * so it must be distinguishable from "untouched".
 */
type BrandDraft = Partial<BrandForm>;

const NEW_BRAND: BrandForm = {
	name: "",
	website: "",
	contactEmail: "",
	contactPhone: "",
	segment: null,
	positioning: null,
	tierBasis: null,
	tierAsOfDate: "",
	manufacturer: "",
	countryOrigin: "",
	isImported: false,
	parentBrandId: null,
	distribution: "",
	isActive: true,
};

/** The saved row as form values -- the base every untouched field reads from. */
function savedValues(brand: AdminBrandDetail | null): BrandForm {
	if (!brand) return NEW_BRAND;
	return {
		name: brand.name ?? "",
		website: brand.website ?? "",
		contactEmail: brand.contactEmail ?? "",
		contactPhone: brand.contactPhone ?? "",
		segment: brand.segment,
		positioning: brand.positioning,
		tierBasis: brand.tierBasis,
		/*
		 * Sliced to the DATE, because the column is a date and the endpoint sends
		 * it as an ISO instant. A `<input type="date">` shows nothing at all for a
		 * value carrying a time, so the field would have read as unset on every
		 * brand that had one.
		 */
		tierAsOfDate: brand.tierAsOfDate ? brand.tierAsOfDate.slice(0, 10) : "",
		manufacturer: brand.manufacturer ?? "",
		countryOrigin: brand.countryOrigin ?? "",
		isImported: brand.isImported ?? false,
		parentBrandId: brand.parentBrandId,
		distribution: (brand.distribution ?? []).join("\n"),
		isActive: brand.isActive,
	};
}

/** The PATCH body: the draft, with empty text boxes turned into clears. */
function buildBody(draft: BrandDraft): BrandInput {
	const body: BrandInput = {};
	for (const key of Object.keys(draft) as (keyof BrandForm)[]) {
		const value = draft[key];
		if (typeof value === "string") {
			/*
			 * `name` trims but never becomes null: it is required, so an emptied one
			 * is an error the server should state, not an instruction to clear a
			 * column that cannot be null.
			 *
			 * It still has to TRIM, which an earlier version did and this one lost
			 * when the branch was written as "everything except name". A name with a
			 * trailing space is stored verbatim AND slips past the duplicate check,
			 * which is `ilike(name)` -- so "Somany " and "Somany" become two brands
			 * that no collision guard will ever put back together.
			 */
			(body as Record<string, unknown>)[key] =
				key === "name" ? value.trim() : orNull(value);
		} else {
			(body as Record<string, unknown>)[key] = value;
		}

		/*
		 * `distribution` is the one field whose form value and wire value are
		 * different SHAPES -- a textarea of lines here, an array of channels there.
		 * Converted after the generic pass rather than inside it, so the
		 * string-clearing rule above still applies to every other text field.
		 *
		 * An emptied box clears the column, matching every other research field:
		 * `null` is how "not known" is recorded, and an empty array would claim the
		 * brand sells through nothing.
		 */
		if (key === "distribution") {
			const lines = String(value ?? "")
				.split("\n")
				.map((line) => line.trim())
				.filter(Boolean);
			(body as Record<string, unknown>).distribution =
				lines.length > 0 ? lines : null;
		}
	}
	return body;
}

/**
 * Empty box means CLEAR, which on the wire is `null` and not `""`.
 *
 * `website` and `contactEmail` are validated with `.url()` and `.email()`, and
 * the empty string satisfies neither -- so sending `""` for a field the
 * operator emptied is a 400 on the one action that was supposed to unset it.
 * `null` is the instruction the schema accepts, and it is distinct from
 * omitting the key, which means "leave alone".
 */
function orNull(value: string): string | null {
	const trimmed = value.trim();
	return trimmed === "" ? null : trimmed;
}

/**
 * A `<Select>` cannot hold `null`, so an explicit "not set" option carries it.
 *
 * Clearing has to be REACHABLE. These are research judgements on an 861-row
 * master that populates unevenly and gets corrected; an editor that can set a
 * segment but never unset one makes the first guess permanent. The API
 * distinguishes the two -- omitting a field means "leave alone", `null` means
 * "clear" -- and this is the control that reaches the second.
 */
function OptionalSelect({
	label,
	value,
	options,
	onChange,
	disabled,
}: {
	label: string;
	value: string | null;
	options: { value: string; label: string }[];
	onChange: (value: string | null) => void;
	disabled?: boolean;
}) {
	return (
		<div className="grid gap-1.5">
			<Label htmlFor={`field-${label}`}>{label}</Label>
			<Select
				value={value ?? UNSET}
				onValueChange={(next) => onChange(next === UNSET ? null : next)}
				disabled={disabled}
			>
				<SelectTrigger id={`field-${label}`} aria-label={label}>
					<SelectValue placeholder="Not set" />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						<SelectItem value={UNSET}>Not set</SelectItem>
						{options.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	);
}

export function BrandEditorPage() {
	const { brandId } = useParams<{ brandId: string }>();

	const {
		data: brand,
		isPending,
		refetch,
	} = useQuery({
		queryKey: ["admin-brand", brandId],
		queryFn: () => getAdminBrand(brandId as string),
		enabled: Boolean(brandId),
	});

	if (brandId && isPending) {
		return (
			<div className="grid gap-3">
				<Skeleton className="h-9 w-64" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	/*
	 * A brand that did not load is NOT a new brand.
	 *
	 * `isPending` goes false the moment the query errors, and there was nothing
	 * after it -- so a 404, a revoked permission or a dropped connection fell
	 * through to the editor with `brand === null`, which is the same input the
	 * create screen gets. The operator saw an empty form that said "Withdraw from
	 * sale" (because a missing row defaults to active), typed into it, and pressed
	 * Save -- which PATCHes the real brand id from the URL, because `isNew` keys
	 * off the id and not off whether anything loaded. Two different screens must
	 * not be reachable by the same render.
	 *
	 * `brands-dashboard.tsx` already answers this case properly; this is its
	 * counterpart.
	 *
	 * The condition is "nothing to show", NOT `isError`, and the difference is
	 * load-bearing. React Query raises `isError` for a failed BACKGROUND refetch
	 * as well, while keeping the data it already has -- so an `isError` branch
	 * tore the editor down and announced "could not load this brand" in the one
	 * moment that is least true: immediately after a save the server accepted.
	 * Keying on absent data leaves that case to the save handler, which knows the
	 * save worked. `product-editor.tsx` draws the same line.
	 */
	if (brandId && !brand) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Could not load this brand</CardTitle>
					<CardDescription>
						It may have been merged away or deleted, or the connection failed.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Button variant="outline" onClick={() => refetch()}>
						Try again
					</Button>
				</CardContent>
			</Card>
		);
	}

	/*
	 * `key` is what resets the draft when the operator opens a different brand.
	 *
	 * React discards a subtree's state when its key changes, which is exactly the
	 * "start again for this brand" behaviour this file previously implemented by
	 * hand: an effect that watched the query result, plus a ref remembering which
	 * id had been seeded so a refetch could not re-run it. Both existed only to
	 * simulate a remount. Asking for the remount is one line and cannot drift out
	 * of step with the data the way the effect did.
	 */
	return (
		<BrandEditor
			key={brandId ?? "new"}
			brandId={brandId}
			brand={brand ?? null}
		/>
	);
}

function BrandEditor({
	brandId,
	brand,
}: {
	brandId?: string;
	brand: AdminBrandDetail | null;
}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);

	const canEdit = hasPermission(operator, "admin:catalog:brands");
	// Withdrawing a brand takes its products off sale, which is why it costs
	// publish rather than brands. Without it the control is DISABLED and labelled
	// with the permission it needs -- not hidden, which this comment used to
	// claim. Naming the missing permission tells a brands-only curator why the
	// button will not move; silently omitting it reads as a broken screen.
	const canWithdraw = hasPermission(operator, "admin:catalog:publish");

	const isNew = !brandId;
	const [draft, setDraft] = useState<BrandDraft>({});
	const [error, setError] = useState<string | null>(null);
	const [mergeOpen, setMergeOpen] = useState(false);
	/** How many products the last merge actually moved, per the server. */
	const [merged, setMerged] = useState<number | null>(null);
	/*
	 * Set the instant a merge returns, so the form locks before the refetch.
	 * The cached row still says `mergedIntoId: null` for a full round trip.
	 */
	const [justMerged, setJustMerged] = useState(false);

	const saved = savedValues(brand);

	/*
	 * `useDirtyState` IS NOT USED HERE, and the deviation is deliberate.
	 *
	 * That hook compares a whole form against a stringified baseline, which is
	 * the right tool for an editor that seeds its state from the loaded row. This
	 * screen does not: `draft` holds ONLY what the operator touched, and setting a
	 * field back to its saved value removes the key again. So an empty draft is
	 * already an exact statement that nothing is unsaved -- stronger than a
	 * stringify comparison, and immune to the timestamp problem the product
	 * editor has to work around.
	 *
	 * Adding the hook on top would mean maintaining a second, weaker answer to a
	 * question this file already answers precisely.
	 */
	const { allowNextNavigation } = useEditorExitGuard(
		Object.keys(draft).length > 0,
	);

	/*
	 * The value on screen: the operator's edit if they made one, otherwise the
	 * server's.
	 *
	 * Nothing copies one into the other, so a background refetch cannot overwrite
	 * an edit -- an untouched field simply starts reading the newer value, which
	 * is what it should do. That is the property the ref guard used to buy.
	 */
	function field<K extends keyof BrandForm>(key: K): BrandForm[K] {
		return key in draft ? (draft[key] as BrandForm[K]) : saved[key];
	}

	/*
	 * Setting a field back to its saved value UN-touches it.
	 *
	 * The draft's whole guarantee is "a key is here only because the operator
	 * changed something", and a set-only draft breaks that the first time anyone
	 * changes their mind. Withdraw then Restore left `isActive: true` pinned in
	 * the draft, so every later save carried it -- and `updateBrand` compares
	 * against the freshly locked row, so an unrelated edit made weeks later would
	 * re-publish a brand somebody else had withdrawn in the meantime, audit-logged
	 * as a restore by whoever happened to fix a typo. That is the exact defect the
	 * draft was introduced to make unreachable, and it came back through the one
	 * path nobody modelled: an edit that returns to where it started.
	 *
	 * Deleting the key makes "changed" mean changed rather than touched, for every
	 * field, so no other control needs to think about it.
	 */
	const set = <K extends keyof BrandForm>(key: K, value: BrandForm[K]) => {
		setDraft((previous) => {
			if (Object.is(value, saved[key])) {
				const { [key]: _reverted, ...rest } = previous;
				return rest as BrandDraft;
			}
			return { ...previous, [key]: value };
		});
	};

	const save = useMutation({
		mutationFn: async () => {
			const body = buildBody(draft);
			if (isNew) {
				const created = await createAdminBrand(body);
				return created.id;
			}
			await updateAdminBrand(brandId as string, body);
			return brandId as string;
		},
		onSuccess: async (id) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["admin-brands"] }),
				queryClient.invalidateQueries({ queryKey: ["admin-brand", id] }),
			]);
			if (isNew) {
				/*
				 * The draft is still full here -- it is what was just saved -- so
				 * without this the guard prompts "You have unsaved changes" about a
				 * brand the server has already accepted, and Cancel strands the
				 * operator on the create screen with the row made.
				 */
				allowNextNavigation();
				navigate(`/catalog/brands/${id}`);
				return;
			}
			/*
			 * Drop the edits only if the refresh actually landed.
			 *
			 * `invalidateQueries` swallows refetch failures -- it catches internally
			 * unless `throwOnError` is set -- so awaiting it proves the attempt
			 * finished, not that it worked. Clearing the draft regardless made every
			 * field fall back to the STALE cached row, so a save the server accepted
			 * appeared on screen to have been undone. Keeping the draft when the
			 * refresh failed leaves the operator looking at what they saved.
			 */
			/*
			 * `error`, not `status`. A background refetch that fails while the cache
			 * still holds data leaves `status` at "success" -- React Query only
			 * reports "error" when there is nothing to show at all -- so keying off
			 * status would have missed exactly the case this guards, a refresh that
			 * failed after a save that worked.
			 */
			if (queryClient.getQueryState(["admin-brand", id])?.error) {
				setError(
					"Saved, but could not refresh. Reload to see the stored copy.",
				);
				return;
			}
			setDraft({});
		},
		onError: (err: unknown) => {
			// The server's own words, INCLUDING its per-field ones. A schema
			// rejection answers a constant "Validation error" in `message` and puts
			// the part that names the field in `errors`, so reading `message` alone
			// turned every bad URL and short country code into one unactionable
			// sentence.
			setError(apiErrorMessage(err, "Could not save this brand."));
		},
	});

	const superseded = Boolean(brand?.mergedIntoId) || justMerged;
	const stagedActive = field("isActive");

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>
							{isNew ? "New brand" : (brand?.name ?? "Brand")}
						</CardTitle>
						<CardDescription>
							{superseded
								? "This brand was merged into another. It is kept so the merge stays traceable, and cannot take new products."
								: "Identity, contact, and the plan-006 brand master."}
						</CardDescription>
					</div>
					{superseded ? <Badge variant="default">Merged away</Badge> : null}
				</CardHeader>

				<CardContent className="grid gap-6">
					{error ? (
						<div
							role="alert"
							className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
						>
							{error}
						</div>
					) : null}

					{merged !== null ? (
						<div
							role="status"
							className="rounded-2xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
						>
							Merged. {merged} product{merged === 1 ? "" : "s"} moved.
						</div>
					) : null}

					{/* ---- identity ------------------------------------------------ */}
					<section className="grid gap-3 md:grid-cols-2">
						<div className="grid gap-1.5">
							<Label htmlFor="name">Name</Label>
							<Input
								id="name"
								value={field("name")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(e) => set("name", e.target.value)}
							/>
						</div>
						<div className="md:col-span-2 md:row-start-2 empty:hidden">
							<BrandNearMatches
								name={"name" in draft ? (draft.name ?? null) : null}
								excludeBrandId={brandId}
							/>
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="website">Website</Label>
							<Input
								id="website"
								value={field("website")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(e) => set("website", e.target.value)}
							/>
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="contactEmail">Contact email</Label>
							<Input
								id="contactEmail"
								value={field("contactEmail")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(e) => set("contactEmail", e.target.value)}
							/>
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="contactPhone">Contact phone</Label>
							<Input
								id="contactPhone"
								value={field("contactPhone")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(e) => set("contactPhone", e.target.value)}
							/>
						</div>
					</section>

					{/* ---- the brand master ---------------------------------------- */}
					<section className="grid gap-3 rounded-2xl border bg-slate-50/60 p-4">
						<div>
							<h3 className="text-sm font-medium">Brand master</h3>
							<p className="text-xs text-muted-foreground">
								Research fields (plan 006). Segment describes the company;
								positioning describes the product line — the largest companies
								span the whole ladder, so they are not the same thing.
							</p>
						</div>

						<div className="grid gap-3 md:grid-cols-2">
							<OptionalSelect
								label="Segment"
								value={field("segment")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(v) => set("segment", v as BrandForm["segment"])}
								options={[
									{ value: "micro", label: "Micro" },
									{ value: "small", label: "Small" },
									{ value: "big", label: "Big" },
									{ value: "large", label: "Large" },
								]}
							/>
							<OptionalSelect
								label="Positioning"
								value={field("positioning")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(v) =>
									set("positioning", v as BrandForm["positioning"])
								}
								options={[
									{ value: "economy", label: "Economy" },
									{ value: "mid", label: "Mid" },
									{ value: "premium", label: "Premium" },
									{ value: "luxury", label: "Luxury" },
								]}
							/>
							<OptionalSelect
								label="Tier basis"
								value={field("tierBasis")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(v) => set("tierBasis", v as BrandForm["tierBasis"])}
								options={[
									{ value: "revenue", label: "Revenue" },
									{ value: "listing", label: "Listing status" },
									{ value: "footprint", label: "Footprint" },
									{ value: "manual", label: "Manual judgement" },
								]}
							/>
							<div className="grid gap-1.5">
								<Label htmlFor="tierAsOfDate">Segment as of</Label>
								<Input
									id="tierAsOfDate"
									type="date"
									value={field("tierAsOfDate")}
									disabled={!canEdit || superseded || save.isPending}
									onChange={(e) => set("tierAsOfDate", e.target.value)}
								/>
								{/*
								 * BESIDE the segment and its basis, not in a section of its
								 * own. Its whole purpose is to be set at the same moment as
								 * the judgement it dates -- a date recorded a screen away is
								 * a date nobody updates when the judgement changes.
								 *
								 * Left blank deliberately when unknown. An undated segment is
								 * a KNOWN state -- most of plan 006's brand master is exactly
								 * that -- so defaulting this to today would turn "nobody has
								 * dated this" into "verified this morning".
								 */}
								<p className="text-xs text-muted-foreground">
									When the segment above was judged. Leave it empty if the
									segment is undated — that is a real answer, not a gap to fill
									with today.
								</p>
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="manufacturer">Manufacturer</Label>
								<Input
									id="manufacturer"
									placeholder="Often not the brand"
									value={field("manufacturer")}
									disabled={!canEdit || superseded || save.isPending}
									onChange={(e) => set("manufacturer", e.target.value)}
								/>
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="countryOrigin">Country of origin</Label>
								<Input
									id="countryOrigin"
									placeholder="Two-letter code, e.g. IN"
									maxLength={2}
									value={field("countryOrigin")}
									disabled={!canEdit || superseded || save.isPending}
									onChange={(e) =>
										set("countryOrigin", e.target.value.toUpperCase())
									}
								/>
							</div>
							<ParentBrandField
								brandId={brandId}
								value={field("parentBrandId")}
								disabled={!canEdit || superseded || save.isPending}
								onChange={(id) => set("parentBrandId", id)}
							/>
							<label className="flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={field("isImported")}
									disabled={!canEdit || superseded || save.isPending}
									onChange={(e) => set("isImported", e.target.checked)}
								/>
								Imported
							</label>
							<div className="grid gap-1.5 md:col-span-2">
								<Label htmlFor="distribution">Distribution</Label>
								<Textarea
									id="distribution"
									rows={3}
									placeholder={"Dealers\nModern trade\nOnline"}
									value={field("distribution")}
									disabled={!canEdit || superseded || save.isPending}
									onChange={(e) => set("distribution", e.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									One channel per line.
								</p>
							</div>
						</div>
					</section>

					{/* ---- lifecycle ----------------------------------------------- */}
					{!isNew && !superseded ? (
						<section className="grid gap-3 rounded-2xl border p-4">
							<div>
								<h3 className="text-sm font-medium">Availability</h3>
								<p className="text-xs text-muted-foreground">
									Withdrawing a brand takes every one of its products out of
									search, browse and new BOQs. Existing BOQ lines keep working.
								</p>
							</div>
							<div className="flex items-center gap-3">
								<Button
									variant={stagedActive === false ? "default" : "outline"}
									size="sm"
									disabled={!canWithdraw || save.isPending}
									onClick={() => {
										// Writing it into the draft is what keeps `isActive` off
										// every other save: the field guard charges on presence,
										// and a key nobody touched is never in the body.
										set("isActive", !stagedActive);
										setError(null);
									}}
								>
									{stagedActive === false
										? "Restore to sale"
										: "Withdraw from sale"}
								</Button>
								{!canWithdraw ? (
									<span className="text-xs text-muted-foreground">
										Needs admin:catalog:publish.
									</span>
								) : null}
							</div>
						</section>
					) : null}

					<div className="flex items-center gap-2 border-t pt-4">
						<Button
							disabled={!canEdit || superseded || save.isPending}
							onClick={() => {
								setError(null);
								save.mutate();
							}}
						>
							{save.isPending ? "Saving…" : "Save"}
						</Button>
						{!isNew && !superseded && canEdit ? (
							<Button variant="outline" onClick={() => setMergeOpen(true)}>
								Merge into another brand
							</Button>
						) : null}
					</div>
				</CardContent>
			</Card>

			{brandId ? (
				<BrandCategoryPanel
					brandId={brandId}
					canEdit={canEdit && !superseded}
				/>
			) : null}

			{mergeOpen && brand ? (
				<MergeBrandDialog
					brand={brand as AdminBrandDetail}
					onClose={() => setMergeOpen(false)}
					onMerged={(result) => {
						setMergeOpen(false);
						// What MOVED, from the server, not the preview's estimate. The
						// merge is irreversible and the two can differ, so the number the
						// operator is left with should be the one that actually happened.
						setMerged(result.movedProducts);
						/*
						 * Marked superseded SYNCHRONOUSLY, not when the refetch lands.
						 *
						 * `superseded` is read from the cached row, and these
						 * invalidations are a network round trip. For that whole window
						 * the form still rendered as a live brand -- every field enabled,
						 * Save enabled, Merge offered again -- against a brand that had
						 * just been merged away. `updateBrand` has no `mergedIntoId`
						 * check of its own, so this control is the only thing stopping
						 * an edit to a superseded row: it cannot be the slower of the
						 * two signals.
						 */
						setJustMerged(true);
						queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
						queryClient.invalidateQueries({
							queryKey: ["admin-brand", brandId],
						});
					}}
				/>
			) : null}
		</div>
	);
}
